import Rails from "@rails/ujs"
import * as bootstrap from "bootstrap"
import TomSelect from 'tom-select'
import Ahoy from 'ahoy.js'
import './admin-user-form'
import { initializeCopyToClipboard } from './copy-to-clipboard'
import './stripe-billing'

Rails.start()

// Track $view events client-side (visits are tracked server-side via after_action)
const AHOY_EXCLUDED_PREFIXES = ['/admin_dashboard', '/admin_api', '/health_check']
document.addEventListener('turbo:load', () => {
  if (!AHOY_EXCLUDED_PREFIXES.some(p => window.location.pathname.startsWith(p))) {
    Ahoy.trackView()
  }
})

// Make Bootstrap available globally for inline scripts
window.bootstrap = bootstrap

// Initialize Tom Select on all elements with data-controller="tom-select"
document.addEventListener('DOMContentLoaded', function() {
  // Initialize copy-to-clipboard buttons
  initializeCopyToClipboard()
  
  document.querySelectorAll('[data-controller="tom-select"]').forEach(function(el) {
    new TomSelect(el, {
      plugins: [],
      maxItems: null,
      persist: false,
      create: false,
      hideSelected: true,
      closeAfterSelect: false,
      placeholder: el.dataset.placeholder || 'Select options...',
      noResultsText: el.dataset['noResultsText'] || 'No results found...'
    })
  })

  // Show hint for free email addresses in user signup form
  // List of common free email providers we want to encourage users to use work/school emails instead
  const freeEmailProviders = [
    'gmail.com',
    'hotmail.com',
    'aol.com',
    'icloud.com',
    'mail.com',
    'protonmail.com',
    'proton.me'
  ]

  // Only target email input in the user signup form
  const signupForm = document.getElementById('user-signup-form')
  const emailInput = signupForm ? signupForm.querySelector('input[type="email"][name="user[email]"]') : null
  if (emailInput) {
    const hintId = 'free-email-hint'
    let hintElement = document.getElementById(hintId)
    
    // Create hint element if it doesn't exist
    if (!hintElement) {
      hintElement = document.createElement('small')
      hintElement.id = hintId
      hintElement.className = 'form-text text-warning'
      hintElement.style.display = 'none'
      // Insert after the email input's parent wrapper
      const inputWrapper = emailInput.closest('.mb-3')
      if (inputWrapper) {
        inputWrapper.appendChild(hintElement)
      }
    }

    function checkEmail() {
      const email = emailInput.value.trim().toLowerCase()
      if (email.length === 0) {
        hintElement.style.display = 'none'
        return
      }

      // Extract domain from email
      const emailParts = email.split('@')
      if (emailParts.length !== 2) {
        hintElement.style.display = 'none'
        return
      }

      const domain = emailParts[1]
      
      // Show hint if domain is a free email provider we want to suggest alternatives for
      if (freeEmailProviders.includes(domain)) {
        hintElement.innerHTML = '<strong>Do you have a work or school email address?</strong> Using your organizational email will be useful if you choose to upgrade to a paid or academic plan.'
        hintElement.style.display = 'block'
      } else {
        hintElement.style.display = 'none'
      }
    }

    // Check on input and blur events
    emailInput.addEventListener('input', checkEmail)
    emailInput.addEventListener('blur', checkEmail)
    
    // Check initial value on page load (in case form is reloaded or has validation errors)
    checkEmail()
  }

  // Admin order form: filter subscription plans and control API token checkbox
  const productSelect = document.getElementById('order_product_id')
  const subscriptionPlanSelect = document.getElementById('order_subscription_plan_id')
  const productsDataEl = document.getElementById('products-data')
  const apiTokenCheckbox = document.getElementById('create_kong_token')
  const apiTokenHelp = document.getElementById('api_token_help')
  
  if (productSelect && subscriptionPlanSelect) {
    // Store all original subscription plan options (excluding the prompt)
    const allPlanOptions = Array.from(subscriptionPlanSelect.options)
      .filter(option => option.value !== '')
      .map(option => ({
        value: option.value,
        text: option.text,
        productId: option.dataset.productId,
        element: option.cloneNode(true)
      }))
    
    const previouslySelectedPlan = subscriptionPlanSelect.value

    // Parse products data if available (for API token checkbox)
    let productsMap = null
    if (productsDataEl) {
      const productsData = JSON.parse(productsDataEl.textContent)
      productsMap = new Map(productsData.map(p => [p.id.toString(), p]))
    }

    function filterSubscriptionPlans(preserveSelection) {
      const selectedProductId = productSelect.value
      
      subscriptionPlanSelect.innerHTML = '<option value="">Select a subscription plan...</option>'
      
      if (selectedProductId) {
        const matchingPlans = []
        allPlanOptions.forEach(plan => {
          if (plan.productId === selectedProductId) {
            subscriptionPlanSelect.appendChild(plan.element.cloneNode(true))
            matchingPlans.push(plan)
          }
        })
        
        if (subscriptionPlanSelect.options.length === 1) {
          const noPlansOption = document.createElement('option')
          noPlansOption.value = ''
          noPlansOption.text = 'No subscription plans available for this product'
          noPlansOption.disabled = true
          subscriptionPlanSelect.appendChild(noPlansOption)
        } else if (matchingPlans.length === 1 && !preserveSelection) {
          subscriptionPlanSelect.value = matchingPlans[0].value
        }
      } else {
        subscriptionPlanSelect.innerHTML = '<option value="">Select a product first...</option>'
      }
      
      if (preserveSelection && previouslySelectedPlan) {
        const optionToSelect = Array.from(subscriptionPlanSelect.options).find(
          opt => opt.value === previouslySelectedPlan
        )
        if (optionToSelect) {
          subscriptionPlanSelect.value = previouslySelectedPlan
        }
      }
      
      // Update API token checkbox when plans change
      updateApiTokenCheckbox()
    }

    function updateApiTokenCheckbox() {
      if (!apiTokenCheckbox || !apiTokenHelp || !productsMap) return
      
      const selectedProductId = productSelect.value
      
      if (!selectedProductId) {
        apiTokenCheckbox.disabled = true
        apiTokenCheckbox.checked = false
        apiTokenHelp.textContent = 'Select a product first'
        return
      }
      
      const product = productsMap.get(selectedProductId)
      
      if (!product) {
        apiTokenCheckbox.disabled = true
        apiTokenCheckbox.checked = false
        apiTokenHelp.textContent = 'Invalid product selection'
        return
      }
      
      apiTokenCheckbox.disabled = false
      apiTokenCheckbox.checked = true
      
      if (product.api_key_from_kong === true) {
        apiTokenHelp.textContent = 'Will be created via Kong consumer and token'
      } else if (product.api_key_from_kong === false) {
        apiTokenHelp.textContent = 'Will be created as internal IWA token'
      } else {
        apiTokenCheckbox.disabled = true
        apiTokenCheckbox.checked = false
        apiTokenHelp.textContent = 'This product does not use API keys'
      }
    }

    // Event listeners
    productSelect.addEventListener('change', function() {
      filterSubscriptionPlans(false)
    })
    
    // Initial setup
    if (productSelect.value) {
      filterSubscriptionPlans(true)
    } else {
      subscriptionPlanSelect.innerHTML = '<option value="">Select a product first...</option>'
      updateApiTokenCheckbox()
    }
  }
})
